import { useState, useCallback, useEffect } from 'react';
import useSWR from 'hooks/useSWR';
import DashboardAPI, { PulseDTO, PulseItemDTO } from 'apis/DashboardAPI';
import { ActivityType } from './sections/pulse/Pulse';

interface PaginatedPulseResult {
  allItems: PulseItemDTO[];
  selection: PulseDTO['selection'];
  loading: boolean;
  hasMore: boolean;
  loadMore: () => void;
}

export function usePaginatedPulseSWR(feedType: ActivityType): PaginatedPulseResult {
  const [allLoadedItems, setAllLoadedItems] = useState<PulseItemDTO[]>([]);
  const [selection, setSelection] = useState<PulseDTO['selection']>('InvestmentPreferences');
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  const [currentFeedType, setCurrentFeedType] = useState<ActivityType>(feedType);
  const [isLoadingNewFeed, setIsLoadingNewFeed] = useState(true); // Start as loading on initial mount

  // Get API URL based on feed type - all pulse feeds don't support pagination
  const getApiUrl = useCallback(() => {
    switch (feedType) {
      case 'For you':
        return DashboardAPI.pulse.platformWide(50);
      case 'From your network':
        return DashboardAPI.pulse.myNetwork();
      case 'From your collections':
        return DashboardAPI.pulse.myCollections();
      default:
        return DashboardAPI.pulse.platformWide(50);
    }
  }, [feedType]);

  // Single SWR call - no pagination for pulse feeds
  const { data: currentData, error: currentError } = useSWR<PulseDTO>(getApiUrl());

  // Track when feed type changes - mark as loading and clear items
  useEffect(() => {
    if (feedType !== currentFeedType) {
      setIsLoadingNewFeed(true);
      setAllLoadedItems([]); // Clear items to avoid showing wrong feed data
      setHasReachedEnd(false);
      setCurrentFeedType(feedType);
    }
  }, [feedType, currentFeedType]);

  // Update items when new data arrives - pulse feeds don't paginate
  // Only update if the data corresponds to the current feed type
  useEffect(() => {
    if (!currentData) return;

    // Only update items if we're looking at data for the current feed type
    // This prevents stale cached data from appearing when switching feeds
    if (feedType === currentFeedType) {
      setAllLoadedItems(currentData.values);
      setSelection(currentData.selection);
      setHasReachedEnd(true); // Pulse feeds don't paginate
      setIsLoadingNewFeed(false); // New feed data has arrived
    }
  }, [currentData, feedType, currentFeedType]);

  // Load more function - no-op for pulse feeds since they don't paginate
  const loadMore = useCallback(() => {
    // Pulse feeds don't support pagination, so this is a no-op
  }, []);

  const loading = (!currentData && !currentError) || isLoadingNewFeed;
  const hasMore = !hasReachedEnd && !currentError;

  return {
    allItems: allLoadedItems,
    selection,
    loading,
    hasMore,
    loadMore,
  };
}
